import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ShieldCheck, TrendingUp, Zap, PackageSearch, Layers, Clock, Truck, CheckSquare, Palette, Eye, Scissors, ClipboardCheck, ChevronRight, ArrowRight, MousePointerClick, UserPlus, Users } from 'lucide-react';
import { categories } from '../lib/categories';
import { cn } from '../lib/utils';
import { BuilderVisual } from '../components/BuilderVisual';
import { BaseballIcon, SoftballIcon, BasketballIcon, VolleyballIcon, FootballIcon, SoccerBallIcon, HockeyIcon, FishingIcon, AccessoriesIcon } from '../components/SportIcons';
// Sport icon map for seasonal supporting cards — keeps each card visually
// distinct (icon-based) instead of reusing the same red jersey image.
const SEASONAL_ICON_MAP: Record<string, FC<{
  className?: string;
  strokeWidth?: number;
}>> = {
  football: FootballIcon,
  soccer: SoccerBallIcon,
  baseball: BaseballIcon,
  softball: SoftballIcon,
  basketball: BasketballIcon,
  volleyball: VolleyballIcon,
  hockey: HockeyIcon,
  fishing: FishingIcon,
  accessories: AccessoriesIcon
};
export const Home = () => <div className="flex flex-col w-full" data-id="element-539">
    <HeroSection data-id="element-540" />
    <AudienceSplitSection data-id="element-541" />
    <CustomizeSection data-id="element-542" />
    <SeasonalSportsSection data-id="element-543" />
    <DealerValueSection data-id="element-544" />
    <QualityProductionSection data-id="element-545" />
    <NeedHelpOrderingSection data-id="element-546" />
    <CategoryGridSection data-id="element-547" />
    <HowItWorksSection data-id="element-548" />
    <FooterCTABanner data-id="element-549" />
  </div>;
const HeroSection = () => <section className="relative bg-white overflow-hidden py-14 lg:py-16 border-b border-gray200" data-id="element-550">
    <div className="absolute top-20 right-0 w-1/3 h-1/3 bg-brandRed/5 rounded-full blur-3xl -z-0" data-id="element-551"></div>

    <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-6 lg:gap-8 items-center relative z-10" data-id="element-552">
      <div className="flex flex-col gap-5" data-id="element-553">
        <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-554">
          <span className="w-8 h-px bg-brandRed" data-id="element-555"></span>
          Wholesale Custom Apparel Platform
        </span>
        <h1 className="text-[40px] leading-[1.05] lg:text-[48px] xl:text-[54px] font-heading font-extrabold text-ink tracking-[-0.025em]" data-id="element-556">
          Wholesale Custom Uniforms Built To Elevate Your Brand
        </h1>
        <p className="text-lg text-gray-600 leading-relaxed" data-id="element-557">
          Factory-direct custom sportswear for dealers, brands, and resellers —
          with account-based designer access for teams and end users who want to
          start building their custom uniform.
        </p>

        <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-3 mt-1" data-id="element-558">
          {['Wholesale production for dealers, brands, and resellers', 'Create an account to access the online designer', 'Teams and end users can start a design online', 'Ordering help available through registered dealers', 'Factory-direct custom uniform production'].map((point, i) => <li key={i} className="flex items-start gap-2.5 text-charcoal font-medium text-[15px] leading-snug" data-id="element-559">
              <CheckCircle2 className="w-5 h-5 text-brandRed shrink-0 mt-0.5" data-id="element-560" />
              <span data-id="element-561">{point}</span>
            </li>)}
        </ul>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-3" data-id="element-562">
          <Link to="/wholesale-apply" className="bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-7 rounded-sm text-center transition-colors shadow-lg shadow-brandRed/20 text-[15px] flex items-center justify-center gap-2 group" data-id="element-563">
            Apply for Wholesale Access
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-564" />
          </Link>
          <Link to="/products" className="bg-white border-2 border-charcoal hover:bg-charcoal hover:text-white text-charcoal font-bold py-3.5 px-7 rounded-sm text-center transition-colors text-[15px]" data-id="element-565">
            Create Account & Start Designing
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1" data-id="element-566">
          <span data-id="element-567">Wholesale/account access may be required for ordering.</span>
          <span className="text-gray-300" data-id="element-568">|</span>
          <Link to="/wholesale-apply" className="text-charcoal font-bold hover:text-brandRed transition-colors flex items-center gap-1" data-id="element-569">
            Need help ordering? <ChevronRight className="w-4 h-4" data-id="element-570" />
          </Link>
        </div>
      </div>

      <div className="relative" data-id="element-571">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} data-id="element-572">
          <BuilderVisual theme="light" data-id="element-573" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="absolute -bottom-5 -left-5 bg-white p-3.5 rounded-lg shadow-xl border border-gray200 hidden md:flex items-center gap-3" data-id="element-574">
          <div className="w-10 h-10 rounded-full bg-brandRed/10 flex items-center justify-center" data-id="element-575">
            <MousePointerClick className="w-5 h-5 text-brandRed" data-id="element-576" />
          </div>
          <div data-id="element-577">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-578">
              Design Online
            </div>
            <div className="text-sm font-bold text-ink" data-id="element-579">
              Preview Instantly in 3D
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
const AudienceSplitSection = () => <section className="relative py-20 lg:py-24 overflow-hidden bg-ink" data-id="element-580">
    {/* Background image — positioned so the coach + tablet stays visible on the right */}
    <div className="absolute inset-0 bg-cover" style={{
    backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/h88JE7g5vQ1kpx17PaeEid/section2_bg.png)',
    backgroundPosition: 'right center'
  }} aria-hidden="true" data-id="element-581" />
    {/* Strong directional overlay — heavy ink on the left for content readability, lighter on the right so the image still reads */}
    <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/90 to-ink/55 lg:to-ink/40" aria-hidden="true" data-id="element-582" />
    {/* Vertical overlay on mobile for additional contrast */}
    <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink/60 lg:hidden" aria-hidden="true" data-id="element-583" />
    {/* Subtle red glow accent */}
    <div className="absolute top-0 left-0 w-1/3 h-1/2 bg-brandRed/15 blur-3xl pointer-events-none" aria-hidden="true" data-id="element-584" />

    <div className="relative z-10 max-w-content mx-auto px-5 sm:px-8 lg:px-12" data-id="element-585">
      <div className="grid lg:grid-cols-[1.85fr_1fr] gap-6 lg:gap-8" data-id="element-586">
        {/* Primary Wholesale Path (65%) */}
        <div className="flex flex-col" data-id="element-587">
          <div className="mb-8" data-id="element-588">
            <span className="text-xs font-bold uppercase tracking-wider text-brandRed drop-shadow-md" data-id="element-589">
              Primary Wholesale Path
            </span>
            <h3 className="text-4xl md:text-5xl font-heading font-bold text-white mt-3 leading-[1.08] drop-shadow-lg" data-id="element-590">
              Wholesale Production Built Behind Your Brand
            </h3>
            <p className="text-gray-200 text-lg mt-4 leading-relaxed drop-shadow-md max-w-2xl" data-id="element-591">
              For dealers, brands, resellers, and apparel businesses that need
              custom uniform production, private-label support, bulk ordering
              tools, and factory-direct production control.
            </p>
          </div>

          <div className="bg-ink rounded-xl p-8 md:p-10 border border-white/15 shadow-2xl relative overflow-hidden flex flex-col" data-id="element-592">
            <div className="absolute top-0 right-0 w-56 h-56 bg-brandRed/15 rounded-full blur-3xl pointer-events-none" data-id="element-593" />
            <div className="relative z-10 flex flex-col h-full" data-id="element-594">
              <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-4 mb-8" data-id="element-595">
                {['Wholesale and dealer production support', 'Private-label uniform manufacturing', 'Bulk team and organization orders', 'Product designer and order tools', 'Built for repeat business and growing apparel programs'].map((benefit, i) => <li key={i} className="flex items-start gap-3 text-gray-100 text-[15px]" data-id="element-596">
                    <CheckCircle2 className="w-5 h-5 text-brandRed shrink-0 mt-0.5" data-id="element-597" />
                    <span data-id="element-598">{benefit}</span>
                  </li>)}
              </ul>

              <div className="mt-auto" data-id="element-599">
                <Link to="/wholesale-apply" className="inline-flex items-center justify-center gap-2 bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-7 rounded-sm transition-colors text-base shadow-lg group" data-id="element-600">
                  Apply for Wholesale Access
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-601" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary End-User Path (35%) */}
        <div className="flex flex-col mt-10 lg:mt-0" data-id="element-602">
          <div className="bg-charcoal/95 backdrop-blur-md rounded-xl p-7 border border-white/15 shadow-xl flex flex-col h-full" data-id="element-603">
            <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-3 self-start" data-id="element-604">
              For Teams & End Users
            </div>
            <h4 className="text-xl md:text-2xl font-heading font-bold text-white mb-3 leading-tight" data-id="element-605">
              Create an Account. Use the Designer. Get Help Ordering.
            </h4>
            <p className="text-[14px] text-gray-300 leading-relaxed mb-6" data-id="element-606">
              Not a dealer? You can still create an account, use the online
              designer, and start building your custom uniform. If you need help
              completing the order, we can help connect you with one of our
              registered dealers.
            </p>

            <ul className="flex flex-col gap-3 mb-8" data-id="element-607">
              {['Create an account to access the designer', 'Customize uniforms online', 'Build colors, logos, names, and numbers', 'Get connected with a registered dealer if you need ordering help'].map((benefit, i) => <li key={i} className="flex items-start gap-2.5 text-gray-200 text-[13px]" data-id="element-608">
                  <CheckCircle2 className="w-4 h-4 text-brandRed shrink-0 mt-0.5" data-id="element-609" />
                  <span data-id="element-610">{benefit}</span>
                </li>)}
            </ul>

            <div className="mt-auto flex flex-col gap-3" data-id="element-611">
              <Link to="/products" className="bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white font-bold py-3 px-5 rounded-sm text-center transition-colors text-sm" data-id="element-612">
                Start Designing
              </Link>
              <Link to="/wholesale-apply" className="text-gray-400 hover:text-white text-[13px] font-medium transition-colors text-center flex items-center justify-center gap-1" data-id="element-613">
                Need help ordering? <ChevronRight className="w-3.5 h-3.5" data-id="element-614" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// Dark customization section — markets the BENEFIT (customization), not any internal tool name
const CustomizeSection = () => <section className="py-20 lg:py-24 bg-ink relative overflow-hidden" data-id="element-615">
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-616">
      <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-brandRed/10 blur-[150px]" data-id="element-617"></div>
      <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-white/5 blur-[120px]" data-id="element-618"></div>
    </div>

    <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12 relative z-10" data-id="element-619">
      <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center" data-id="element-620">
        <div className="flex flex-col gap-5" data-id="element-621">
          <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-622">
            <span className="w-8 h-px bg-brandRed" data-id="element-623"></span>
            Online Uniform Designer
          </span>
          <h2 className="text-4xl md:text-[44px] lg:text-[50px] font-heading font-bold text-white leading-[1.08] tracking-tight" data-id="element-624">
            Customize Your Uniform.
            <br data-id="element-625" />
            See It Instantly in 3D.
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed" data-id="element-626">
            Create an account to access the online designer and start building
            custom uniforms with colors, logos, names, numbers, and product
            options.
          </p>

          <ul className="flex flex-col gap-3 mt-1" data-id="element-627">
            {[{
            icon: Eye,
            text: 'Create an account to unlock the designer'
          }, {
            icon: Palette,
            text: 'Build uniforms in the online designer'
          }, {
            icon: Layers,
            text: 'Preview colors, logos, names, and numbers'
          }, {
            icon: ClipboardCheck,
            text: 'Dealers can use the tool for client mockups'
          }, {
            icon: Clock,
            text: 'Teams and end users can design first, then get ordering help if needed'
          }].map((feature, i) => <li key={i} className="flex items-center gap-3.5 text-gray-100 font-medium text-[15px]" data-id="element-628">
                <div className="w-10 h-10 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0" data-id="element-629">
                  <feature.icon className="w-5 h-5 text-brandRed" data-id="element-630" />
                </div>
                <span data-id="element-631">{feature.text}</span>
              </li>)}
          </ul>

          <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-632">
            <Link to="/products" className="inline-flex items-center justify-center gap-2 bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-7 rounded-sm transition-colors shadow-lg shadow-brandRed/20 text-[15px] group" data-id="element-633">
              Create Account & Start Designing
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-634" />
            </Link>
            <Link to="/wholesale-apply" className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/20 hover:border-white text-white font-bold py-3.5 px-7 rounded-sm transition-colors text-[15px]" data-id="element-635">
              Apply for Wholesale Access
            </Link>
          </div>
          <div className="mt-1" data-id="element-636">
            <Link to="/wholesale-apply" className="text-gray-400 hover:text-white text-sm font-medium transition-colors flex items-center gap-1" data-id="element-637">
              Need help with an order? <ChevronRight className="w-4 h-4" data-id="element-638" />
            </Link>
          </div>
        </div>

        <div className="relative" data-id="element-639">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-640">
            <BuilderVisual theme="dark" data-id="element-641" />
          </motion.div>
        </div>
      </div>
    </div>
  </section>;
const SeasonalSportsSection = () => {
  const activeSports = ['football', 'soccer', 'baseball', 'softball', 'basketball', 'volleyball'];
  const getCat = (slug: string) => categories.find(c => c.slug === slug);
  return <section className="py-14 lg:py-16 bg-white border-t border-gray200" data-id="element-642">
      <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12" data-id="element-643">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8" data-id="element-644">
          <div className="max-w-2xl" data-id="element-645">
            <span className="text-xs font-bold uppercase tracking-wider text-brandRed" data-id="element-646">
              Seasonal Shortcuts
            </span>
            <h2 className="text-3xl md:text-[34px] font-heading font-bold text-ink mt-2" data-id="element-647">
              Featured Seasonal Uniforms
            </h2>
            <p className="text-[15px] text-gray-600 mt-2" data-id="element-648">
              Jump straight into the sports currently in season.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 self-start md:self-auto bg-gray100 border border-gray200 px-3.5 py-2 rounded-md" data-id="element-649">
            <span className="w-1.5 h-1.5 rounded-full bg-brandRed" data-id="element-650" />
            <span className="text-sm font-bold text-ink" data-id="element-651">Featured Now</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-id="element-652">
          {activeSports.slice(0, 6).map(slug => {
          const cat = getCat(slug);
          if (!cat) return null;
          const SportIcon = SEASONAL_ICON_MAP[slug] || cat.icon as FC<{
            className?: string;
            strokeWidth?: number;
          }> | undefined;
          return <Link key={slug} to={`/category/${slug}`} className="group relative rounded-lg overflow-hidden bg-white border border-gray200 hover:border-charcoal hover:shadow-md transition-all flex flex-col aspect-[5/6]" data-id="element-653">
                {/* Image / icon area */}
                <div className="relative flex-1 bg-gray50 overflow-hidden" data-id="element-654">
                  {cat.image ? <img src={cat.image} alt={cat.name} className="absolute inset-0 w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500" data-id="element-655" /> : SportIcon ? <div className="absolute inset-0 flex items-center justify-center" data-id="element-656">
                      <SportIcon className="w-14 h-14 text-charcoal group-hover:text-brandRed transition-colors" strokeWidth={1.5} data-id="element-657" />
                    </div> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-658">
                      <div className="w-10 h-10 rounded-full bg-brandRed/30" data-id="element-659" />
                    </div>}
                </div>

                {/* Label */}
                <div className="p-3 border-t border-gray100 bg-white" data-id="element-660">
                  <h4 className="text-sm font-heading font-bold text-ink leading-tight" data-id="element-661">
                    {cat.name}
                  </h4>
                  <span className="inline-flex items-center gap-0.5 text-[12px] font-bold text-brandRed mt-1 group-hover:gap-1.5 transition-all" data-id="element-662">
                    Start Designing
                    <ChevronRight className="w-3 h-3" data-id="element-663" />
                  </span>
                </div>
              </Link>;
        })}
        </div>
      </div>
    </section>;
};
const SportIconCard = ({
  category,
  large = false
}: {
  category: (typeof categories)[0];
  large?: boolean;
}) => {
  const Icon = category.icon;
  return <div className="absolute inset-0 bg-gradient-to-br from-charcoal to-ink flex items-center justify-center" data-id="element-664">
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
      backgroundSize: large ? '40px 40px' : '20px 20px'
    }} data-id="element-665"></div>
      {Icon && <Icon className={cn('text-brandRed/40', large ? 'w-32 h-32' : 'w-12 h-12')} strokeWidth={1.5} data-id="element-666" />}
    </div>;
};
const DealerValueSection = () => {
  const benefits = [{
    icon: ShieldCheck,
    title: 'Private-label production',
    desc: 'No-logo production protects your brand identity. We stay behind the scenes.'
  }, {
    icon: TrendingUp,
    title: 'Wholesale pricing',
    desc: 'Built specifically to protect and maximize dealer margins.'
  }, {
    icon: Layers,
    title: 'Combined quantity pricing',
    desc: 'Mix and match items to reach higher discount tiers across your package.'
  }, {
    icon: Zap,
    title: 'Speed Rush production',
    desc: 'Fast turnarounds available on many no-button items.'
  }, {
    icon: PackageSearch,
    title: 'Increased production capacity',
    desc: 'Reliable fulfillment for large volume orders.'
  }, {
    icon: Scissors,
    title: 'Additional fabric options',
    desc: 'More choices for texture, stretch, and performance.'
  }, {
    icon: Clock,
    title: 'USA-time support',
    desc: 'Dedicated support team available 9am–10pm EST.'
  }, {
    icon: Truck,
    title: 'Drop-ship-friendly',
    desc: 'Direct fulfillment options to simplify your logistics.'
  }, {
    icon: CheckSquare,
    title: 'Quality control',
    desc: 'Rigorous checks at multiple production levels.'
  }];
  return <section className="py-20 lg:py-24 bg-gray50 border-y border-gray200" data-id="element-667">
      <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12" data-id="element-668">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-669">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-670">
            Built for Dealers
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3 mb-5 leading-tight" data-id="element-671">
            Built for Dealers Who Need Better Margins and Fewer Production
            Headaches
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed" data-id="element-672">
            Custom Uniform Supplier helps dealers and resellers sell custom
            apparel under their own brand while relying on T1 production,
            support, and fulfillment behind the scenes.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" data-id="element-673">
          {benefits.map((benefit, i) => <div key={i} className="bg-white p-7 rounded-xl border border-gray200 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 group" data-id="element-674">
              <div className="w-14 h-14 rounded-lg bg-gray50 border border-gray100 flex items-center justify-center mb-5 group-hover:bg-brandRed/5 group-hover:border-brandRed/20 transition-colors" data-id="element-675">
                <benefit.icon className="w-7 h-7 text-charcoal group-hover:text-brandRed transition-colors" strokeWidth={1.75} data-id="element-676" />
              </div>
              <h3 className="text-xl font-bold text-ink mb-2.5" data-id="element-677">
                {benefit.title}
              </h3>
              <p className="text-[15px] text-gray-600 leading-relaxed" data-id="element-678">
                {benefit.desc}
              </p>
            </div>)}
        </div>

        <div className="bg-ink rounded-2xl p-10 md:p-12 text-white flex flex-col md:flex-row items-start md:items-center gap-8 shadow-2xl relative overflow-hidden" data-id="element-679">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brandRed/10 rounded-full blur-3xl" data-id="element-680"></div>
          <div className="w-20 h-20 rounded-2xl bg-brandRed flex items-center justify-center shrink-0 relative z-10" data-id="element-681">
            <TrendingUp className="w-10 h-10 text-white" strokeWidth={2} data-id="element-682" />
          </div>
          <div className="relative z-10" data-id="element-683">
            <h4 className="text-2xl md:text-3xl font-heading font-bold mb-3" data-id="element-684">
              How Combined Quantity Pricing Works
            </h4>
            <p className="text-gray-200 leading-relaxed text-[17px] max-w-3xl" data-id="element-685">
              If a dealer orders 100 shirts and 100 shorts, they can qualify for
              200-piece pricing instead of two separate 100-piece tiers. This
              helps dealers quote full packages more competitively.
            </p>
          </div>
        </div>

        <div className="mt-8 lg:mt-10 bg-white border border-gray200 border-l-4 border-l-brandRed rounded-r-xl p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5 md:gap-8 shadow-sm" data-id="element-686">
          <div className="w-12 h-12 rounded-full bg-brandRed/10 flex items-center justify-center shrink-0 hidden md:flex" data-id="element-687">
            <Users className="w-6 h-6 text-brandRed" data-id="element-688" />
          </div>
          <div className="flex-1" data-id="element-689">
            <div className="text-xs font-bold uppercase tracking-wider text-brandRed mb-1" data-id="element-690">
              Not a dealer yet?
            </div>
            <p className="text-[15px] text-ink font-medium leading-relaxed" data-id="element-691">
              Teams, coaches, organizations, and end users can still create an
              account and use the designer. If you need help placing an order,
              we can help connect you with a registered dealer.
            </p>
          </div>
          <Link to="/products" className="bg-white border-2 border-charcoal hover:bg-charcoal hover:text-white text-charcoal font-bold py-2.5 px-6 rounded-sm transition-colors text-sm whitespace-nowrap self-start md:self-auto" data-id="element-692">
            Start Designing
          </Link>
        </div>
      </div>
    </section>;
};
const QualityProductionSection = () => {
  const checks = [{
    icon: Palette,
    title: 'Art Review',
    desc: 'Detailed artwork review and setup.'
  }, {
    icon: Eye,
    title: 'Color Matching',
    desc: 'Accurate color reproduction.'
  }, {
    icon: Zap,
    title: 'Heat Press Checks',
    desc: 'Verified application quality.'
  }, {
    icon: Layers,
    title: 'Sorting',
    desc: 'Organized by team, size, and roster.'
  }, {
    icon: Scissors,
    title: 'Finishing',
    desc: 'Final inspection of every piece.'
  }, {
    icon: Truck,
    title: 'Drop-Ship Fulfillment',
    desc: 'Ready to ship to your customer.'
  }];
  return <section className="py-20 lg:py-24 bg-white" data-id="element-693">
      <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12" data-id="element-694">
        <div className="text-center mb-14 max-w-3xl mx-auto" data-id="element-695">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-696">
            Quality Control
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3 mb-5 leading-tight" data-id="element-697">
            Production Support Built Around Accuracy
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed" data-id="element-698">
            Extensive quality control at multiple levels helps protect your
            artwork, colors, sizing, sorting, and finishing before orders ship.
          </p>
          <p className="text-[15px] text-gray-500 mt-3 leading-relaxed" data-id="element-699">
            Whether you are a dealer managing client orders or an end user
            starting a team design, our process helps keep artwork, sizing,
            approvals, and production details organized.
          </p>
        </div>

        <div className="max-w-5xl mx-auto mb-16" data-id="element-700">
          <div className="text-center mb-6" data-id="element-701">
            <span className="text-sm font-bold uppercase tracking-wider text-gray-400" data-id="element-702">
              Approval Process
            </span>
          </div>
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 relative" data-id="element-703">
            <div className="hidden md:block absolute top-1/2 left-[10%] right-[10%] h-0.5 bg-gray200 -z-0 -translate-y-1/2" data-id="element-704"></div>

            {[{
            step: '1',
            title: 'Artwork Approval',
            desc: 'Initial art review'
          }, {
            step: '2',
            title: 'Mini Approval',
            desc: 'Small sample check'
          }, {
            step: '3',
            title: 'Full-Size Sample Approval',
            desc: 'Final production review'
          }].map((phase, i) => <div key={i} className="flex-1 bg-white border-2 border-ink rounded-xl px-6 py-5 flex items-center gap-4 shadow-sm relative z-10" data-id="element-705">
                <div className="w-11 h-11 rounded-full bg-brandRed text-white text-base font-bold flex items-center justify-center shrink-0" data-id="element-706">
                  {phase.step}
                </div>
                <div data-id="element-707">
                  <div className="font-bold text-ink text-base" data-id="element-708">
                    {phase.title}
                  </div>
                  <div className="text-sm text-gray-500 mt-0.5" data-id="element-709">
                    {phase.desc}
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-710">
          {checks.map((check, i) => <div key={i} className="bg-gray50 border border-gray200 rounded-xl p-5 hover:border-brandRed/30 hover:bg-white transition-colors" data-id="element-711">
              <div className="w-11 h-11 rounded-lg bg-white border border-gray200 flex items-center justify-center mb-4" data-id="element-712">
                <check.icon className="w-5 h-5 text-brandRed" data-id="element-713" />
              </div>
              <h4 className="font-bold text-ink text-[15px] mb-1.5" data-id="element-714">
                {check.title}
              </h4>
              <p className="text-[13px] text-gray-600 leading-snug" data-id="element-715">
                {check.desc}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
};
const NeedHelpOrderingSection = () => {
  return <section className="py-20 lg:py-24 bg-gray50 border-t border-gray200" data-id="element-716">
      <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12" data-id="element-717">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-718">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-719">
            End-User & Team Support
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3 mb-4" data-id="element-720">
            Need Help Turning Your Design Into an Order?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed" data-id="element-721">
            If you are a team, coach, organization, or end user and need help
            completing your custom uniform order, Custom Uniform Supplier can
            help get you set up with one of our registered dealers.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5" data-id="element-722">
          <div className="bg-white rounded-xl border border-gray200 p-7 shadow-sm flex flex-col items-start" data-id="element-723">
            <div className="w-10 h-10 rounded-full bg-brandRed text-white font-bold flex items-center justify-center mb-5 shrink-0" data-id="element-724">
              1
            </div>
            <div className="w-12 h-12 rounded-lg bg-gray50 border border-gray100 flex items-center justify-center mb-4" data-id="element-725">
              <UserPlus className="w-6 h-6 text-brandRed" strokeWidth={1.5} data-id="element-726" />
            </div>
            <h3 className="text-xl font-bold text-ink mb-2" data-id="element-727">
              Create Your Account
            </h3>
            <p className="text-[15px] text-gray-600 leading-relaxed" data-id="element-728">
              Sign up so you can access the designer and start building your
              uniform.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray200 p-7 shadow-sm flex flex-col items-start" data-id="element-729">
            <div className="w-10 h-10 rounded-full bg-brandRed text-white font-bold flex items-center justify-center mb-5 shrink-0" data-id="element-730">
              2
            </div>
            <div className="w-12 h-12 rounded-lg bg-gray50 border border-gray100 flex items-center justify-center mb-4" data-id="element-731">
              <Palette className="w-6 h-6 text-brandRed" strokeWidth={1.5} data-id="element-732" />
            </div>
            <h3 className="text-xl font-bold text-ink mb-2" data-id="element-733">
              Design Your Uniform
            </h3>
            <p className="text-[15px] text-gray-600 leading-relaxed" data-id="element-734">
              Choose your sport, colors, logos, names, numbers, and product
              options.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray200 p-7 shadow-sm flex flex-col items-start" data-id="element-735">
            <div className="w-10 h-10 rounded-full bg-brandRed text-white font-bold flex items-center justify-center mb-5 shrink-0" data-id="element-736">
              3
            </div>
            <div className="w-12 h-12 rounded-lg bg-gray50 border border-gray100 flex items-center justify-center mb-4" data-id="element-737">
              <Users className="w-6 h-6 text-brandRed" strokeWidth={1.5} data-id="element-738" />
            </div>
            <h3 className="text-xl font-bold text-ink mb-2" data-id="element-739">
              Get Dealer Support
            </h3>
            <p className="text-[15px] text-gray-600 leading-relaxed" data-id="element-740">
              If you need help placing the order, we can connect you with a
              registered dealer who can assist.
            </p>
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-741">
          <Link to="/wholesale-apply" className="bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-8 rounded-sm transition-colors text-base shadow-lg flex items-center gap-2 group w-full sm:w-auto justify-center" data-id="element-742">
            Get Help With My Order
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-743" />
          </Link>
          <Link to="/products" className="bg-white border-2 border-charcoal hover:bg-charcoal hover:text-white text-charcoal font-bold py-3.5 px-8 rounded-sm transition-colors text-base w-full sm:w-auto text-center" data-id="element-744">
            Start Designing
          </Link>
        </div>
      </div>
    </section>;
};
const CategoryGridSection = () => <section className="py-20 lg:py-24 bg-gray50 border-t border-gray200" data-id="element-745">
    <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12" data-id="element-746">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10" data-id="element-747">
        <div data-id="element-748">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-749">
            Wholesale Catalog
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3" data-id="element-750">
            Browse All Categories
          </h2>
        </div>
        <Link to="/products" className="text-base font-bold text-charcoal hover:text-brandRed transition-colors flex items-center gap-1.5 group" data-id="element-751">
          View Full Catalog
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-752" />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-753">
        {categories.map(cat => <Link key={cat.slug} to={`/category/${cat.slug}`} className="group bg-white border border-gray200 rounded-xl overflow-hidden hover:border-charcoal transition-all hover:-translate-y-1 hover:shadow-xl flex flex-col" data-id="element-754">
            <div className="aspect-square bg-gray100 relative overflow-hidden" data-id="element-755">
              {cat.image ? <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-756" /> : <IconCategoryCard category={cat} data-id="element-757" />}
            </div>
            <div className="p-4 bg-white" data-id="element-758">
              <div className="flex items-center justify-between mb-1" data-id="element-759">
                <span className="font-bold text-ink text-base" data-id="element-760">{cat.name}</span>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brandRed transition-colors" data-id="element-761" />
              </div>
              {cat.shortLabel && <p className="text-[13px] text-gray-500 leading-tight" data-id="element-762">
                  {cat.shortLabel}
                </p>}
            </div>
          </Link>)}
      </div>
    </div>
  </section>;
// Premium branded icon card for categories without proper imagery (Fishing, Hockey, Soccer, Accessories)
const IconCategoryCard = ({
  category
}: {
  category: (typeof categories)[0];
}) => {
  const Icon = category.icon;
  return <div className="w-full h-full bg-gradient-to-br from-ink via-charcoal to-ink flex items-center justify-center relative overflow-hidden" data-id="element-763">
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-764"></div>
      {/* Red glow */}
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-brandRed/20 rounded-full blur-3xl" data-id="element-765"></div>
      <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-brandRed/10 rounded-full blur-3xl" data-id="element-766"></div>

      {/* Icon block */}
      {Icon && <div className="relative z-10 flex flex-col items-center gap-3" data-id="element-767">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-white/15 to-white/5 border border-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl" data-id="element-768">
            <Icon className="w-10 h-10 text-white" strokeWidth={1.5} data-id="element-769" />
          </div>
          <div className="px-3 py-1 rounded-full bg-brandRed/20 border border-brandRed/30 backdrop-blur-sm" data-id="element-770">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white" data-id="element-771">
              Custom {category.name}
            </span>
          </div>
        </div>}

      {/* Corner brand badge */}
      <div className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-wider text-white/60 bg-white/5 px-2 py-1 rounded backdrop-blur-sm border border-white/10" data-id="element-772">
        Wholesale
      </div>
    </div>;
};
const HowItWorksSection = () => {
  const steps = [{
    title: 'Choose a sport or product',
    desc: 'Browse our extensive wholesale catalog.'
  }, {
    title: 'Start your design online',
    desc: 'Customize colors, patterns, and fabrics in 3D.'
  }, {
    title: 'Submit for wholesale quote/support',
    desc: 'Send your design to our team for review.'
  }, {
    title: 'Review art, sizing, and production details',
    desc: 'Finalize specs with our USA-based support.'
  }, {
    title: 'Approve the final production steps',
    desc: 'Sign off on artwork and samples.'
  }, {
    title: 'Receive private-label custom gear',
    desc: 'Ready for your customer or team.'
  }];
  return <section className="py-20 lg:py-24 bg-white" data-id="element-773">
      <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12" data-id="element-774">
        <div className="text-center mb-14 max-w-3xl mx-auto" data-id="element-775">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-776">
            Process
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3 mb-4 leading-tight" data-id="element-777">
            From Design to Production Support
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed" data-id="element-778">
            Built for dealers and resellers who want T1 production behind their
            own customer-facing brand.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-779">
          {steps.map((step, i) => <div key={i} className="flex gap-5 p-7 rounded-xl border border-gray200 bg-gray50 hover:bg-white hover:border-brandRed/30 hover:shadow-md transition-all" data-id="element-780">
              <div className="shrink-0" data-id="element-781">
                <div className="w-12 h-12 rounded-lg bg-ink text-white font-heading font-extrabold text-xl flex items-center justify-center" data-id="element-782">
                  {i + 1}
                </div>
              </div>
              <div className="flex-1" data-id="element-783">
                <h3 className="text-[17px] font-bold text-ink mb-2 leading-tight" data-id="element-784">
                  {step.title}
                </h3>
                <p className="text-[15px] text-gray-600 leading-relaxed" data-id="element-785">
                  {step.desc}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const FooterCTABanner = () => <section className="relative py-20 lg:py-24 bg-ink overflow-hidden" data-id="element-786">
    <div className="absolute inset-0 bg-gradient-to-br from-ink via-charcoal to-brandRed/20" data-id="element-787"></div>
    <div className="absolute top-0 right-0 w-1/2 h-full bg-brandRed/10 blur-[100px] rounded-full transform translate-x-1/2" data-id="element-788"></div>
    <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-white/5 blur-[80px] rounded-full transform -translate-x-1/2" data-id="element-789"></div>

    <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 text-center" data-id="element-790">
      <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight" data-id="element-791">
        Ready to Start Your Custom Uniform Order?
      </h2>
      <p className="text-xl text-gray-300 mb-10 leading-relaxed" data-id="element-792">
        Apply for wholesale access, create an account to start designing, or get
        help connecting with a registered dealer.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-793">
        <Link to="/wholesale-apply" className="bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-8 rounded-sm transition-colors text-lg shadow-lg shadow-brandRed/20 flex items-center gap-2 group w-full sm:w-auto justify-center" data-id="element-794">
          Apply for Wholesale Access
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-795" />
        </Link>
        <Link to="/products" className="bg-transparent border-2 border-white/20 hover:border-white text-white font-bold py-3.5 px-8 rounded-sm transition-colors text-lg w-full sm:w-auto text-center" data-id="element-796">
          Start Designing
        </Link>
      </div>
      <div className="mt-6" data-id="element-797">
        <Link to="/wholesale-apply" className="text-gray-400 hover:text-white text-sm font-medium transition-colors inline-flex items-center gap-1" data-id="element-798">
          Need Help Ordering? <ChevronRight className="w-4 h-4" data-id="element-799" />
        </Link>
      </div>
    </div>
  </section>;